import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { ArrowLeft, Share2, Copy, HardHat, QrCode, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";
import { ContractorDefectGroups } from "./defects/ContractorDefectGroups";

export default function ContractorDefects() {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [defects, setDefects] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedContractor, setSelectedContractor] = useState("");
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [printing, setPrinting] = useState(false);

  const token = () => localStorage.getItem("token");

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      const res = await fetch(`${API}/sites`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSites(data.sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (e) {
      console.error("Error loading sites:", e);
    }
  };

  const handleSiteChange = async (siteId) => {
    setSelectedSite(siteId);
    setSelectedContractor("");
    setDefects([]);
    try {
      // Load companies AND the site's defects in parallel so we can list
      // contractors that are linked to the site OR have defects assigned there.
      const [companiesRes, defectsRes] = await Promise.all([
        fetch(`${API}/companies?active_only=true`, {
          headers: { Authorization: `Bearer ${token()}` },
        }),
        fetch(`${API}/defects?site_id=${siteId}`, {
          headers: { Authorization: `Bearer ${token()}` },
        }),
      ]);

      const companies = companiesRes.ok ? await companiesRes.json() : [];
      const siteDefects = defectsRes.ok ? await defectsRes.json() : [];
      const assignedIds = new Set(
        siteDefects.map((d) => d.assigned_to).filter(Boolean)
      );

      const list = companies
        .filter((c) => {
          const isContractorType =
            c.company_type === "contractor" || c.company_type === "subcontractor";
          const linkedToSite =
            Array.isArray(c.site_ids) && c.site_ids.includes(siteId);
          const hasDefectsHere = assignedIds.has(c.id);
          return (isContractorType && linkedToSite) || hasDefectsHere;
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      setContractors(list);
    } catch (e) {
      console.error("Error loading contractors:", e);
    }
  };

  const handleContractorChange = async (contractorId) => {
    setSelectedContractor(contractorId);
    await loadDefects(selectedSite, contractorId);
  };

  const loadDefects = async (siteId, contractorId) => {
    if (!siteId || !contractorId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/defects?site_id=${siteId}&assigned_to=${contractorId}`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setDefects(data);
      }
    } catch (e) {
      console.error("Error loading defects:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSetStatus = async (defectId, status) => {
    setUpdatingId(defectId);
    try {
      const res = await fetch(`${API}/defects/${defectId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        toast.success(
          status === "completed" ? "Marked as complete" : "Marked as incomplete"
        );
        await loadDefects(selectedSite, selectedContractor);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update status");
      }
    } catch (e) {
      console.error("Error updating status:", e);
      toast.error("Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const getShareUrl = async () => {
    try {
      const res = await fetch(`${API}/contractor-defects/share-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({
          site_id: selectedSite,
          company_id: selectedContractor,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        return `${window.location.origin}/contractor-defects/${data.token}`;
      }
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Failed to create share link");
    } catch (e) {
      console.error("Error creating share link:", e);
      toast.error("Failed to create share link");
    }
    return null;
  };

  const handleShareLink = async () => {
    const url = await getShareUrl();
    if (url) {
      setShareUrl(url);
      setShowShareDialog(true);
    }
  };

  const handleViewQr = async () => {
    const url = await getShareUrl();
    if (!url) return;
    try {
      const dataUrl = await QRCode.toDataURL(url, { width: 400, margin: 2 });
      setShareUrl(url);
      setQrDataUrl(dataUrl);
      setShowQrDialog(true);
    } catch (e) {
      console.error("Error generating QR code:", e);
      toast.error("Failed to generate QR code");
    }
  };

  const downloadQr = () => {
    const siteName = sites.find((s) => s.id === selectedSite)?.name || "";
    const qrImg = new Image();
    qrImg.onload = () => {
      const qrSize = 400;
      const pad = 24;
      const lh = 30;
      const canvas = document.createElement("canvas");
      canvas.width = qrSize + pad * 2;
      canvas.height = qrSize + pad * 2 + lh * 2;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(qrImg, pad, pad, qrSize, qrSize);
      ctx.textAlign = "center";

      // Fit a line of text within the canvas width by shrinking the font if needed
      const drawFitted = (text, y, maxFont, minFont, weight, color) => {
        let size = maxFont;
        ctx.fillStyle = color;
        do {
          ctx.font = `${weight} ${size}px Arial, sans-serif`;
          if (ctx.measureText(text).width <= canvas.width - 16) break;
          size -= 1;
        } while (size > minFont);
        ctx.fillText(text, canvas.width / 2, y);
      };

      drawFitted(
        `Defects for Contractor: ${contractorName}`,
        qrSize + pad + lh - 6,
        20,
        12,
        "bold",
        "#0f172a"
      );
      drawFitted(`at ${siteName}`, qrSize + pad + lh * 2 - 8, 16, 11, "normal", "#475569");

      const safeName = (contractorName || "qr")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `contractor-defects-${safeName}.png`;
      link.click();
    };
    qrImg.src = qrDataUrl;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard");
  };

  const handlePrintReport = async () => {
    if (expandedCategories.length === 0) {
      toast.error("Expand at least one category to include in the report");
      return;
    }
    setPrinting(true);
    try {
      const res = await fetch(`${API}/contractor-defects/report-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({
          site_id: selectedSite,
          company_id: selectedContractor,
          categories: expandedCategories,
          generated_at: new Date().toLocaleString("en-AU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to generate report");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const safe = (contractorName || "contractor")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      const link = document.createElement("a");
      link.href = url;
      link.download = `defects-report-${safe}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      console.error("Error generating report:", e);
      toast.error("Failed to generate report");
    } finally {
      setPrinting(false);
    }
  };

  const contractorName =
    contractors.find((c) => c.id === selectedContractor)?.name || "";
  const selectedSiteName = sites.find((s) => s.id === selectedSite)?.name || "";

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/defects")}
          data-testid="contractor-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <HardHat className="w-6 h-6 text-amber-500" />
            Contractor Defects Management
          </h1>
          <p className="text-sm text-slate-500">
            Select a site and contractor to view and update their assigned defects.
          </p>
        </div>
      </div>

      <Card className="dashboard-card mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <Label>Site</Label>
              <Select value={selectedSite} onValueChange={handleSiteChange}>
                <SelectTrigger data-testid="contractor-site-select">
                  <SelectValue placeholder="Select a site..." />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contractor</Label>
              <Select
                value={selectedContractor}
                onValueChange={handleContractorChange}
                disabled={!selectedSite}
              >
                <SelectTrigger data-testid="contractor-company-select">
                  <SelectValue
                    placeholder={
                      selectedSite ? "Select a contractor..." : "Select a site first"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {contractors.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <Button
                  onClick={handleShareLink}
                  disabled={!selectedSite || !selectedContractor}
                  variant="outline"
                  className="flex-1"
                  data-testid="contractor-share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Share Link
                </Button>
                <Button
                  onClick={handleViewQr}
                  disabled={!selectedSite || !selectedContractor}
                  variant="outline"
                  className="flex-1"
                  data-testid="contractor-qr-btn"
                >
                  <QrCode className="w-4 h-4 mr-2" /> View QR Code
                </Button>
              </div>
              <Button
                onClick={handlePrintReport}
                disabled={
                  !selectedSite ||
                  !selectedContractor ||
                  expandedCategories.length === 0 ||
                  printing
                }
                className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="contractor-print-report-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                {printing ? "Generating..." : "Print Report to PDF"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedContractor && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-slate-800">
              {contractorName} — {defects.length} defect
              {defects.length !== 1 ? "s" : ""}
            </h2>
          </div>
          {loading ? (
            <p className="text-slate-500">Loading...</p>
          ) : defects.length === 0 ? (
            <Card className="dashboard-card">
              <CardContent className="p-8 text-center text-slate-500">
                No defects assigned to this contractor on this site.
              </CardContent>
            </Card>
          ) : (
            <ContractorDefectGroups
              defects={defects}
              onSetStatus={handleSetStatus}
              updatingId={updatingId}
              testid="contractor-defects-list"
              onExpandedChange={setExpandedCategories}
            />
          )}
        </div>
      )}

      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contractor Share Link</DialogTitle>
            <DialogDescription>
              Send this link to {contractorName} so they can view and update their
              assigned defects without logging in.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 py-2">
            <Input value={shareUrl} readOnly data-testid="contractor-share-url" />
            <Button onClick={copyToClipboard} data-testid="contractor-copy-btn">
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showQrDialog} onOpenChange={setShowQrDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contractor QR Code</DialogTitle>
            <DialogDescription>
              {contractorName} can scan this code to open their assigned defects.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            {qrDataUrl && (
              <img
                src={qrDataUrl}
                alt="Contractor defects QR code"
                className="w-64 h-64 border rounded-lg"
                data-testid="contractor-qr-image"
              />
            )}
            <div className="text-center">
              <p className="font-semibold text-slate-900" data-testid="contractor-qr-label-name">
                Defects for Contractor: {contractorName}
              </p>
              <p className="text-sm text-slate-500">at {selectedSiteName}</p>
            </div>
            <p className="text-xs text-slate-500 break-all text-center">{shareUrl}</p>
            <Button
              onClick={downloadQr}
              variant="outline"
              className="w-full"
              data-testid="contractor-qr-download-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Download QR Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
